import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    MenuItem,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tabs,
    Tab,
    Skeleton,
    Chip,
    Divider,
    InputAdornment
} from '@mui/material';
import {
    Download as DownloadIcon,
    Print as PrintIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { reportsAPI, membersAPI } from '../api/api';
import { formatCurrency, formatDate, getCategoryStyle } from '../config';

const REPORT_TITLES = [
    'Statement of Accounts',
    'Income Statement',
    'Expense Statement',
    'Balance Sheet',
    'Member Breakdown'
];

const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
        {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
);

const reportTableSx = {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch'
};

const formatNumber = (value) => Number(value || 0).toFixed(2);

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const getTransactionType = (txn) => txn.expense_type_name || txn.income_group || txn.category_name || 'Other';

const getSortValue = (txn, sortBy) => {
    switch (sortBy) {
        case 'transaction_date':
            return txn.transaction_date ? new Date(txn.transaction_date).getTime() : 0;
        case 'type':
            return getTransactionType(txn).toLowerCase();
        case 'member':
            return (txn.member_name || '').toLowerCase();
        case 'share_percentage':
            return Number(txn.share_percentage || 0);
        case 'share_amount':
            return Number(txn.share_amount || 0);
        case 'particulars':
            return (txn.particulars || '').toLowerCase();
        default:
            return txn[sortBy] || '';
    }
};

const Reports = () => {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        memberId: ''
    });
    const [transactionTableState, setTransactionTableState] = useState({
        search: '',
        member: '',
        type: '',
        sortBy: 'transaction_date',
        sortDirection: 'desc'
    });
    const [reportData, setReportData] = useState(null);

    useEffect(() => {
        loadMembers();
    }, []);

    const loadMembers = async () => {
        try {
            const response = await membersAPI.getActive();
            if (response.data.success) {
                setMembers(response.data.data);
            }
        } catch (error) {
            console.error('Error loading members:', error);
        }
    };

    const generateReport = async () => {
        try {
            setLoading(true);
            let response;

            switch (tabValue) {
                case 0:
                    response = await reportsAPI.getSOA(filters);
                    break;
                case 1:
                    response = await reportsAPI.getIncomeStatement(filters);
                    break;
                case 2:
                    response = await reportsAPI.getExpenseStatement(filters);
                    break;
                case 3:
                    response = await reportsAPI.getBalanceSheet({ asOfDate: filters.endDate });
                    break;
                case 4:
                    if (!filters.memberId) {
                        setReportData(null);
                        return;
                    }
                    response = await reportsAPI.getMemberBreakdown(filters);
                    break;
                default:
                    return;
            }

            if (response.data.success) {
                setReportData(response.data.data);
            }
        } catch (error) {
            console.error('Error generating report:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const resetTransactionTable = () => {
        setTransactionTableState({
            search: '',
            member: '',
            type: '',
            sortBy: 'transaction_date',
            sortDirection: 'desc'
        });
    };

    const updateTransactionTableState = (updates) => {
        setTransactionTableState((current) => ({ ...current, ...updates }));
    };

    const handleSort = (sortBy) => {
        setTransactionTableState((current) => ({
            ...current,
            sortBy,
            sortDirection: current.sortBy === sortBy && current.sortDirection === 'desc' ? 'asc' : 'desc'
        }));
    };

    const prepareTransactionRows = (transactions = []) => {
        const search = transactionTableState.search.trim().toLowerCase();
        const filtered = transactions.filter((txn) => {
            const type = getTransactionType(txn);
            const member = txn.member_name || '';
            const searchable = [
                formatDate(txn.transaction_date),
                txn.particulars,
                type,
                member,
                txn.share_percentage,
                txn.share_amount
            ].join(' ').toLowerCase();

            return (!search || searchable.includes(search))
                && (!transactionTableState.type || type === transactionTableState.type)
                && (!transactionTableState.member || member === transactionTableState.member);
        });

        return [...filtered].sort((a, b) => {
            const first = getSortValue(a, transactionTableState.sortBy);
            const second = getSortValue(b, transactionTableState.sortBy);

            if (first < second) return transactionTableState.sortDirection === 'asc' ? -1 : 1;
            if (first > second) return transactionTableState.sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const getIncomeRows = () => [
        ...(reportData?.salesIncome || []).map((txn) => ({ ...txn, income_group: 'Sales Income' })),
        ...(reportData?.investmentIncome || []).map((txn) => ({ ...txn, income_group: 'Investment Income' }))
    ];

    const buildExportSheets = () => {
        if (!reportData) return [];

        switch (tabValue) {
            case 0:
                return [
                    {
                        name: 'Member Summary',
                        columns: ['Member', 'Total Income', 'Total Expenses', 'Balance'],
                        rows: (reportData.memberSummary || []).map((m) => [
                            m.name,
                            m.totalIncome,
                            m.totalExpense,
                            m.balance
                        ])
                    },
                    {
                        name: 'Transaction Details',
                        columns: ['Date', 'Particulars', 'Category', 'Member', 'Share Amount'],
                        rows: prepareTransactionRows(reportData.transactions || []).map((txn) => [
                            formatDate(txn.transaction_date),
                            txn.particulars,
                            txn.category_name,
                            txn.member_name,
                            txn.share_amount
                        ])
                    }
                ];
            case 1:
                return [
                    {
                        name: 'Income Transactions',
                        columns: ['Date', 'Particulars', 'Income Group', 'Category', 'Member', 'Share Amount'],
                        rows: prepareTransactionRows(getIncomeRows()).map((txn) => [
                            formatDate(txn.transaction_date),
                            txn.particulars,
                            txn.income_group,
                            txn.category_name,
                            txn.member_name,
                            txn.share_amount
                        ])
                    },
                    {
                        name: 'Income Totals',
                        columns: ['Metric', 'Amount'],
                        rows: [
                            ['Sales Income', reportData.totals?.totalSales],
                            ['Investment Income', reportData.totals?.totalInvestment],
                            ['Grand Total', reportData.totals?.grandTotal]
                        ]
                    }
                ];
            case 2:
                return [
                    {
                        name: 'Expense Transactions',
                        columns: ['Date', 'Particulars', 'Expense Type', 'Division Basis', 'Member', 'Share %', 'Share Amount'],
                        rows: prepareTransactionRows(reportData.transactions || []).map((txn) => [
                            formatDate(txn.transaction_date),
                            txn.particulars,
                            txn.expense_type_name || 'Other',
                            txn.division_basis,
                            txn.member_name,
                            formatNumber(txn.share_percentage),
                            txn.share_amount
                        ])
                    },
                    {
                        name: 'Expense Type Totals',
                        columns: ['Expense Type', 'Total'],
                        rows: (reportData.byExpenseType || []).map((group) => [group.name, group.total])
                    }
                ];
            case 3:
                return [
                    {
                        name: 'Member Balances',
                        columns: ['Member', 'Income', 'Expenses', 'Balance'],
                        rows: (reportData.members || []).map((m) => [
                            m.name,
                            m.total_income,
                            m.total_expenses,
                            m.balance
                        ])
                    },
                    {
                        name: 'Receivables',
                        columns: ['Customer', 'Outstanding'],
                        rows: (reportData.receivables || []).map((c) => [c.name, c.outstanding])
                    },
                    {
                        name: 'Summary',
                        columns: ['Metric', 'Amount'],
                        rows: [
                            ['Total Income', reportData.summary?.totalIncome],
                            ['Total Expenses', reportData.summary?.totalExpenses],
                            ['Net Balance', reportData.summary?.netBalance],
                            ['Total Receivables', reportData.summary?.totalReceivables],
                            ['Total Assets', reportData.summary?.totalAssets]
                        ]
                    }
                ];
            case 4:
                return [
                    {
                        name: 'Member Summary',
                        columns: ['Member', 'PVC Pipes', 'Pepper Plants', 'Total Income', 'Total Expenses', 'Transaction Count'],
                        rows: [[
                            reportData.member?.name,
                            reportData.member?.pvc_pipes,
                            reportData.member?.pepper_plants,
                            reportData.summary?.totalIncome,
                            reportData.summary?.totalExpenses,
                            reportData.summary?.transactionCount
                        ]]
                    },
                    {
                        name: 'Transactions',
                        columns: ['Date', 'Particulars', 'Type', 'Share %', 'Amount'],
                        rows: prepareTransactionRows(reportData.transactions || []).map((txn) => [
                            formatDate(txn.transaction_date),
                            txn.particulars,
                            txn.expense_type_name || txn.category_name,
                            formatNumber(txn.share_percentage),
                            txn.share_amount
                        ])
                    }
                ];
            default:
                return [];
        }
    };

    const handleExportExcel = () => {
        const sheets = buildExportSheets();
        if (!sheets.length) return;

        const title = REPORT_TITLES[tabValue];
        const period = [filters.startDate, filters.endDate].filter(Boolean).join(' to ');
        const html = `
            <html>
                <head>
                    <meta charset="utf-8" />
                    <style>
                        table { border-collapse: collapse; margin-bottom: 24px; }
                        th, td { border: 1px solid #999; padding: 6px 8px; font-family: Arial, sans-serif; font-size: 12px; }
                        th { background: #e8f5e9; font-weight: bold; }
                        h1, h2, p { font-family: Arial, sans-serif; }
                    </style>
                </head>
                <body>
                    <h1>${escapeHtml(title)}</h1>
                    ${period ? `<p>Period: ${escapeHtml(period)}</p>` : ''}
                    ${sheets.map((sheet) => `
                        <h2>${escapeHtml(sheet.name)}</h2>
                        <table>
                            <thead>
                                <tr>${sheet.columns.map((col) => `<th>${escapeHtml(col)}</th>`).join('')}</tr>
                            </thead>
                            <tbody>
                                ${sheet.rows.map((row) => `
                                    <tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>
                                `).join('')}
                            </tbody>
                        </table>
                    `).join('')}
                </body>
            </html>
        `;

        const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const link = document.createElement('a');
        const safeTitle = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        link.href = URL.createObjectURL(blob);
        link.download = `${safeTitle || 'report'}-${new Date().toISOString().slice(0, 10)}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    const renderSortableHeader = (label, sortBy, align = 'left') => (
        <TableCell align={align}>
            <TableSortLabel
                active={transactionTableState.sortBy === sortBy}
                direction={transactionTableState.sortBy === sortBy ? transactionTableState.sortDirection : 'asc'}
                onClick={() => handleSort(sortBy)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );

    const renderTransactionTable = (transactions = [], options = {}) => {
        const visibleRows = prepareTransactionRows(transactions);
        const typeOptions = [...new Set(transactions.map(getTransactionType).filter(Boolean))].sort();
        const memberOptions = [...new Set(transactions.map((txn) => txn.member_name).filter(Boolean))].sort();

        return (
            <>
                <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} className="no-print">
                    <Grid item xs={12} md={4}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search transactions"
                            value={transactionTableState.search}
                            onChange={(e) => updateTransactionTableState({ search: e.target.value })}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label={options.typeLabel || 'Type'}
                            value={transactionTableState.type}
                            onChange={(e) => updateTransactionTableState({ type: e.target.value })}
                        >
                            <MenuItem value="">All</MenuItem>
                            {typeOptions.map((type) => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {options.showMember !== false && (
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="Member"
                                value={transactionTableState.member}
                                onChange={(e) => updateTransactionTableState({ member: e.target.value })}
                            >
                                <MenuItem value="">All</MenuItem>
                                {memberOptions.map((member) => (
                                    <MenuItem key={member} value={member}>{member}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}
                    <Grid item xs={12} md={2}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {visibleRows.length} of {transactions.length}
                        </Typography>
                    </Grid>
                </Grid>

                <TableContainer sx={reportTableSx}>
                    <Table size="small" className="report-table">
                        <TableHead>
                            <TableRow>
                                {renderSortableHeader('Date', 'transaction_date')}
                                {renderSortableHeader('Particulars', 'particulars')}
                                {renderSortableHeader(options.typeLabel || 'Category', 'type')}
                                {options.showMember !== false && renderSortableHeader('Member', 'member')}
                                {options.showSharePercentage && renderSortableHeader('Share %', 'share_percentage', 'right')}
                                {renderSortableHeader(options.amountLabel || 'Share Amount', 'share_amount', 'right')}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map((txn, idx) => (
                                <TableRow key={`${txn.id || idx}-${txn.member_id || txn.member_name || ''}`}>
                                    <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                                    <TableCell>{txn.particulars}</TableCell>
                                    <TableCell>
                                        {options.useChip ? (
                                            <Chip
                                                label={txn.category_name}
                                                size="small"
                                                sx={{
                                                    bgcolor: getCategoryStyle(txn.category_name).bg,
                                                    color: getCategoryStyle(txn.category_name).text,
                                                    border: `1px solid ${getCategoryStyle(txn.category_name).border}`,
                                                    fontWeight: 600,
                                                    fontSize: 10,
                                                    height: 18
                                                }}
                                            />
                                        ) : (
                                            getTransactionType(txn)
                                        )}
                                    </TableCell>
                                    {options.showMember !== false && <TableCell>{txn.member_name || '-'}</TableCell>}
                                    {options.showSharePercentage && (
                                        <TableCell align="right">{formatNumber(txn.share_percentage)}%</TableCell>
                                    )}
                                    <TableCell align="right" sx={{
                                        color: txn.category_type === 'EXPENSE' ? 'error.main' : 'success.main'
                                    }}>
                                        {formatCurrency(txn.share_amount)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        );
    };

    const renderSOA = () => {
        if (!reportData) return null;

        return (
            <>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Member Summary</Typography>
                        <TableContainer sx={reportTableSx}>
                            <Table size="small" className="report-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Member</TableCell>
                                        <TableCell align="right">Total Income</TableCell>
                                        <TableCell align="right">Total Expenses</TableCell>
                                        <TableCell align="right">Balance</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.memberSummary?.map((member) => (
                                        <TableRow key={member.id}>
                                            <TableCell>{member.name}</TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main' }}>
                                                {formatCurrency(member.totalIncome)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'error.main' }}>
                                                {formatCurrency(member.totalExpense)}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontWeight: 600,
                                                color: member.balance >= 0 ? 'success.main' : 'error.main'
                                            }}>
                                                {formatCurrency(member.balance)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell><strong>Total</strong></TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                                            {formatCurrency(reportData.totals?.totalIncome)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                                            {formatCurrency(reportData.totals?.totalExpense)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {formatCurrency(reportData.totals?.netBalance)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Transaction Details ({reportData.transactions?.length || 0})
                        </Typography>
                        {renderTransactionTable(reportData.transactions, { useChip: true })}
                    </CardContent>
                </Card>
            </>
        );
    };

    const renderIncomeStatement = () => {
        if (!reportData) return null;
        const rows = getIncomeRows();

        return (
            <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <Card><CardContent>
                            <Typography variant="caption" color="text.secondary">Sales Income</Typography>
                            <Typography variant="h6" color="success.main">{formatCurrency(reportData.totals?.totalSales)}</Typography>
                        </CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card><CardContent>
                            <Typography variant="caption" color="text.secondary">Investment Income</Typography>
                            <Typography variant="h6" color="success.main">{formatCurrency(reportData.totals?.totalInvestment)}</Typography>
                        </CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card><CardContent>
                            <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                            <Typography variant="h6" color="primary.main">{formatCurrency(reportData.totals?.grandTotal)}</Typography>
                        </CardContent></Card>
                    </Grid>
                </Grid>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Income Transactions ({rows.length})</Typography>
                        {renderTransactionTable(rows, { typeLabel: 'Income Group' })}
                    </CardContent>
                </Card>
            </>
        );
    };

    const renderExpenseStatement = () => {
        if (!reportData) return null;

        return (
            <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <Card><CardContent>
                            <Typography variant="caption" color="text.secondary">Grand Total Expenses</Typography>
                            <Typography variant="h6" color="error.main">{formatCurrency(reportData.totals?.grandTotal)}</Typography>
                        </CardContent></Card>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Card><CardContent>
                            <Typography variant="caption" color="text.secondary">Expense Type Totals</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {reportData.byExpenseType?.map((group) => (
                                    <Chip key={group.name} label={`${group.name}: ${formatCurrency(group.total)}`} size="small" />
                                ))}
                            </Box>
                        </CardContent></Card>
                    </Grid>
                </Grid>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Expense Transactions ({reportData.transactions?.length || 0})
                        </Typography>
                        {renderTransactionTable(reportData.transactions, {
                            typeLabel: 'Expense Type',
                            showSharePercentage: true
                        })}
                    </CardContent>
                </Card>
            </>
        );
    };

    const renderBalanceSheet = () => {
        if (!reportData) return null;

        return (
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Member Balances</Typography>
                            <TableContainer sx={reportTableSx}>
                                <Table size="small" className="report-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Member</TableCell>
                                            <TableCell align="right">Income</TableCell>
                                            <TableCell align="right">Expenses</TableCell>
                                            <TableCell align="right">Balance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.members?.map((member) => (
                                            <TableRow key={member.id}>
                                                <TableCell>{member.name}</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                                    {formatCurrency(member.total_income)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                                    {formatCurrency(member.total_expenses)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {formatCurrency(member.balance)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Receivables</Typography>
                            {reportData.receivables?.length > 0 ? (
                                <TableContainer sx={reportTableSx}>
                                    <Table size="small" className="report-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Customer</TableCell>
                                                <TableCell align="right">Outstanding</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData.receivables.map((cust) => (
                                                <TableRow key={cust.id}>
                                                    <TableCell>{cust.name}</TableCell>
                                                    <TableCell align="right">{formatCurrency(cust.outstanding)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No outstanding receivables</Typography>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Summary</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Total Income</Typography>
                                <Typography color="success.main" fontWeight={500}>
                                    {formatCurrency(reportData.summary?.totalIncome)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Total Expenses</Typography>
                                <Typography color="error.main" fontWeight={500}>
                                    {formatCurrency(reportData.summary?.totalExpenses)}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Net Balance</Typography>
                                <Typography fontWeight={600}>
                                    {formatCurrency(reportData.summary?.netBalance)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography>Total Receivables</Typography>
                                <Typography fontWeight={500}>
                                    {formatCurrency(reportData.summary?.totalReceivables)}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="h6">Total Assets</Typography>
                                <Typography variant="h6" color="primary" fontWeight={700}>
                                    {formatCurrency(reportData.summary?.totalAssets)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    const renderMemberBreakdown = () => {
        if (!reportData) return null;

        return (
            <>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {reportData.member?.name} - Summary
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">PVC Pipes</Typography>
                                <Typography variant="h6">{reportData.member?.pvc_pipes}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Pepper Plants</Typography>
                                <Typography variant="h6">{reportData.member?.pepper_plants}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Total Income</Typography>
                                <Typography variant="h6" color="success.main">
                                    {formatCurrency(reportData.summary?.totalIncome)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary">Total Expenses</Typography>
                                <Typography variant="h6" color="error.main">
                                    {formatCurrency(reportData.summary?.totalExpenses)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Transactions ({reportData.transactions?.length || 0})
                        </Typography>
                        {renderTransactionTable(reportData.transactions, {
                            typeLabel: 'Type',
                            showMember: false,
                            showSharePercentage: true,
                            amountLabel: 'Amount'
                        })}
                    </CardContent>
                </Card>
            </>
        );
    };

    return (
        <Box sx={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                <Typography variant="h4" fontWeight={700}>
                    Reports
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} className="no-print">
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        disabled={!reportData || loading}
                    >
                        Export Excel
                    </Button>
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} disabled={!reportData || loading}>
                        Print
                    </Button>
                </Box>
            </Box>

            <Card sx={{ mb: 2 }} className="no-print">
                <Tabs
                    value={tabValue}
                    onChange={(e, v) => { setTabValue(v); setReportData(null); resetTransactionTable(); }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {REPORT_TITLES.map((title) => <Tab key={title} label={title} />)}
                </Tabs>
            </Card>

            <Card sx={{ mb: 2 }} className="no-print">
                <CardContent>
                    <Grid container spacing={1.5} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Start Date"
                                type="date"
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={filters.startDate}
                                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="End Date"
                                type="date"
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={filters.endDate}
                                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                            />
                        </Grid>
                        {tabValue === 4 && (
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Member"
                                    select
                                    size="small"
                                    fullWidth
                                    value={filters.memberId}
                                    onChange={(e) => setFilters({ ...filters, memberId: e.target.value })}
                                >
                                    {members.map((m) => (
                                        <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}
                        <Grid item xs={12} sm={3}>
                            <Button variant="contained" onClick={generateReport} fullWidth disabled={loading}>
                                {loading ? 'Generating...' : 'Generate Report'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {loading ? (
                [...Array(3)].map((_, i) => <Skeleton key={i} height={100} sx={{ mb: 2 }} />)
            ) : (
                <>
                    <TabPanel value={tabValue} index={0}>{renderSOA()}</TabPanel>
                    <TabPanel value={tabValue} index={1}>{renderIncomeStatement()}</TabPanel>
                    <TabPanel value={tabValue} index={2}>{renderExpenseStatement()}</TabPanel>
                    <TabPanel value={tabValue} index={3}>{renderBalanceSheet()}</TabPanel>
                    <TabPanel value={tabValue} index={4}>{renderMemberBreakdown()}</TabPanel>
                </>
            )}

            {!reportData && !loading && (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 5 }}>
                        <Typography color="text.secondary">
                            Select filters and click "Generate Report" to view the report
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default Reports;
